import { useEffect } from "react";
import {
  captureClickIdsFromSearch,
  getAllPersistedClickIds,
  resolveUtmParam,
} from "~/helpers/clickIds";

const getLocalStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.localStorage;
};

const setUTM = (key: string, value: string) => {
  try {
    const storage = getLocalStorage();
    if (typeof storage?.setItem === "function") {
      storage.setItem(key, value);
    }
  } catch {
    return;
  }
};

const getUTMValue = (key: string) => {
  try {
    const storage = getLocalStorage();
    const value =
      typeof storage?.getItem === "function" ? storage.getItem(key) : null;
    return value || null;
  } catch {
    return null;
  }
};

export const useUTM = () => {
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const capturedClickIds = captureClickIdsFromSearch(searchParams);
    const utmCampaign = resolveUtmParam(
      searchParams,
      "utm_campaign",
      "utm_campaign_name"
    );
    const utmCampaignid = resolveUtmParam(
      searchParams,
      "utm_campaignid",
      "utm_id",
      "utm_campaign_id",
      "campaignid"
    );
    const utmSource = searchParams.get("utm_source");
    const utmMedium = searchParams.get("utm_medium");
    const utmTerm = resolveUtmParam(searchParams, "utm_term", "keyword");
    const utmContent = searchParams.get("utm_content");
    const utmMediumVariant = searchParams.get("utm_medium_variant");
    const utmDevice = searchParams.get("utm_device");
    const utmNetwork = searchParams.get("utm_network");
    const utmPlacement = searchParams.get("utm_placement");
    const utmLocPhysical = searchParams.get("utm_loc_physical");
    const utmAdgroup = resolveUtmParam(
      searchParams,
      "utm_adgroup",
      "utm_adgroup_name",
      "utm_adgroup_id",
      "adgroupid"
    );
    const utmAssetgroupid = searchParams.get("utm_assetgroupid");
    const utmCreative = searchParams.get("utm_creative");
    const utmKeyword = resolveUtmParam(
      searchParams,
      "utm_keyword",
      "keyword",
      "utm_term"
    );
    const utmKeywordid = searchParams.get("utm_keywordid");
    const utmSearchterm = searchParams.get("utm_searchterm");
    const utmMatchtype = resolveUtmParam(
      searchParams,
      "utm_matchtype",
      "matchtype"
    );
    const utmLocation = searchParams.get("utm_location");
    const utmSitelink = searchParams.get("utm_sitelink");

    const hasUtms = !!(
      utmCampaign ||
      utmCampaignid ||
      utmSource ||
      utmMedium ||
      utmTerm ||
      utmContent ||
      utmMediumVariant ||
      utmDevice ||
      utmNetwork ||
      utmPlacement ||
      utmLocPhysical ||
      utmAdgroup ||
      utmAssetgroupid ||
      utmCreative ||
      utmKeyword ||
      utmKeywordid ||
      utmSearchterm ||
      utmMatchtype ||
      utmLocation ||
      utmSitelink
    );

    if (!hasUtms && !Object.keys(capturedClickIds).length) return;

    // Only write UTM keys that arrived on this landing (avoid blanking first-touch)
    const utmWrites: Array<[string, string | null]> = [
      ["utm_campaign", utmCampaign],
      ["utm_campaignid", utmCampaignid],
      ["utm_source", utmSource],
      ["utm_medium", utmMedium],
      ["utm_term", utmTerm],
      ["utm_content", utmContent],
      ["utm_medium_variant", utmMediumVariant],
      ["utm_device", utmDevice],
      ["utm_network", utmNetwork],
      ["utm_placement", utmPlacement],
      ["utm_loc_physical", utmLocPhysical],
      ["utm_adgroup", utmAdgroup],
      ["utm_assetgroupid", utmAssetgroupid],
      ["utm_creative", utmCreative],
      ["utm_keyword", utmKeyword],
      ["utm_keywordid", utmKeywordid],
      ["utm_searchterm", utmSearchterm],
      ["utm_matchtype", utmMatchtype],
      ["utm_location", utmLocation],
      ["utm_sitelink", utmSitelink],
    ];

    for (const [key, value] of utmWrites) {
      if (value) {
        setUTM(key, value);
      }
    }
  }, []);

  const clickIds = getAllPersistedClickIds();

  return {
    campaign: getUTMValue("utm_campaign"),
    campaignid: getUTMValue("utm_campaignid"),
    source: getUTMValue("utm_source"),
    medium: getUTMValue("utm_medium"),
    term: getUTMValue("utm_term"),
    content: getUTMValue("utm_content"),
    medium_variant: getUTMValue("utm_medium_variant"),
    device: getUTMValue("utm_device"),
    network: getUTMValue("utm_network"),
    placement: getUTMValue("utm_placement"),
    loc_physical: getUTMValue("utm_loc_physical"),
    adgroup: getUTMValue("utm_adgroup"),
    assetgroupid: getUTMValue("utm_assetgroupid"),
    creative: getUTMValue("utm_creative"),
    keyword: getUTMValue("utm_keyword"),
    keywordid: getUTMValue("utm_keywordid"),
    searchterm: getUTMValue("utm_searchterm"),
    matchtype: getUTMValue("utm_matchtype"),
    location: getUTMValue("utm_location"),
    sitelink: getUTMValue("utm_sitelink"),
    gclid: clickIds.gclid,
    gbraid: clickIds.gbraid,
    wbraid: clickIds.wbraid,
  };
};
